<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { trans } from '@tools/translation.ts';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import Button from '@components/Button.vue';
import { IconArrowCounterClockwiseFillBlack } from '@icons';

const { isOpen } = defineProps<{ isOpen: boolean }>();

const emit = defineEmits<{(e: 'update:isOpen', value: boolean): void}>();

const containerRef = ref<HTMLElement | null>(null);
let isPointerDownInside = false;

const handlePointerDown = (event: PointerEvent): void => {
  if (!containerRef.value) {
    return;
  }

  isPointerDownInside = containerRef.value.contains(event.target as Node);
};

const handlePointerUp = (event: PointerEvent): void => {
  if (!isOpen || !containerRef.value) {
    return;
  }

  const isPointerUpInside = containerRef.value.contains(event.target as Node);

  if (!isPointerDownInside && !isPointerUpInside) {
    emit('update:isOpen', false);
  }

  isPointerDownInside = false;
};

onMounted(() => {
  document.addEventListener('pointerdown', handlePointerDown);
  document.addEventListener('pointerup', handlePointerUp);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handlePointerDown);
  document.removeEventListener('pointerup', handlePointerUp);
});
</script>

<template>
  <div
    ref="containerRef"
    class="relative"
  >
    <Button
      :class="[
        '!flex gap-2',
        { '!border-blue !bg-blue-lightest': isOpen }
      ]"
      @click="emit('update:isOpen', !isOpen)"
    >
      <slot name="icon" />

      <Typography variant="body-bold" :color="isOpen ? 'blue' : 'black'">
        <slot name="label" />
      </Typography>
    </Button>

    <div
      v-if="isOpen"
      :class="[
        'absolute left-0 top-full z-50 mt-3',
        'rounded border border-solid border-gray shadow-md',
        'bg-white'
      ]"
    >
      <slot name="panel" />

      <div
        :class="[
          'border-x-0 border-b-0 border-t border-solid border-gray',
          'flex justify-between p-3'
        ]"
      >
        <Button
          variant="invisible"
          :class="[
            '!w-fit',
            '!flex !items-center',
            '!px-1 !py-0.5',
          ]"
        >
          <Icon
            :svg="IconArrowCounterClockwiseFillBlack"
            width="24"
            height="24"
            class="mx-2"
          />

          <Typography variant="body-bold">
            {{ trans('search.filters.reset') }}
          </Typography>
        </Button>

        <Button
          class="!w-fit whitespace-nowrap"
          variant="primary"
        >
          {{ trans('modal.filters.button') }}
        </Button>
      </div>
    </div>
  </div>
</template>
